import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const alertVariants = cva(
  "relative w-full rounded-lg border px-4 py-3 text-sm grid has-[>svg]:grid-cols-[calc(var(--spacing)*4)_1fr] grid-cols-[0_1fr] has-[>svg]:gap-x-3 gap-y-0.5 items-start [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      variant: {
        default: "bg-card text-card-foreground",
        destructive:
          "text-destructive bg-card [&>svg]:text-current *:data-[slot=alert-description]:text-destructive/90",
        info:
          "bg-blue-50 border-blue-200 text-blue-900 [&>svg]:text-blue-600 *:data-[slot=alert-description]:text-blue-800 dark:bg-blue-500/10 dark:border-blue-500/20 dark:text-blue-400 dark:[&>svg]:text-blue-400 dark:*:data-[slot=alert-description]:text-blue-400/80",
        warning:
          "bg-amber-50 border-amber-200 text-amber-900 [&>svg]:text-amber-600 *:data-[slot=alert-description]:text-amber-800 dark:bg-yellow-500/10 dark:border-yellow-500/20 dark:text-yellow-400 dark:[&>svg]:text-yellow-400 dark:*:data-[slot=alert-description]:text-yellow-400/80",
        success:
          "bg-green-50 border-green-200 text-green-900 [&>svg]:text-green-600 *:data-[slot=alert-description]:text-green-800 dark:bg-green-500/10 dark:border-green-500/20 dark:text-green-400 dark:[&>svg]:text-green-400 dark:*:data-[slot=alert-description]:text-green-400/80",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

function Alert({
  className,
  variant,
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof alertVariants>) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        "col-start-2 line-clamp-1 min-h-4 font-medium tracking-tight",
        className
      )}
      {...props}
    />
  )
}

function AlertDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        "text-muted-foreground col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed",
        className
      )}
      {...props}
    />
  )
}

export { Alert, AlertTitle, AlertDescription }
